import React, { useCallback, useEffect, useState } from 'react';
import { Boxes } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import DashboardDetails from '../components/AhriDashboard/DashboardDetails';
import DashboardHero from '../components/AhriDashboard/DashboardHero';
import LoadingSkeleton from '../components/AhriDashboard/LoadingSkeleton';
import PipelineTimeline from '../components/AhriDashboard/PipelineTimeline';
import { usePipelineAutoRefresh } from '../components/AhriDashboard/usePipelineAutoRefresh';
import { fmt, fmtDate, statusBadgeClass } from '../components/AhriDashboard/formatters';
import '../styles/ahri-dashboard.css';

function AhriDashboard() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pipelineMessage, setPipelineMessage] = useState(null);

  const loadDashboard = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) {
      setLoading(true);
    }
    setError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/dashboard`, {
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Request failed (${resp.status})`);
      }
      setData(await resp.json());
    } catch (err) {
      setError(err.message || 'Unable to load dashboard');
      setData(null);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const pipelineDisplayTick = usePipelineAutoRefresh(data?.pipeline?.steps, {
    onRefresh: () => loadDashboard({ silent: true }),
    hasActiveJob: data?.pipeline?.has_active_job,
  });

  const ahri = data?.ahri_data || {};
  const ahriSync = ahri?.last_sync || {};
  const ahriBreakdown = ahriSync?.breakdown || {};
  const matchTotals = ahri?.match_totals || {};
  const ahriSolr = data?.ahri_solr || {};
  const mappingJob = data?.product_mapping?.last_job || {};
  const productDb = data?.products?.database || {};
  const productIndex = data?.products?.index || {};
  const productV4 = data?.products?.v4_import || null;
  const indexState = productIndex?.primary_engine === 'opensearch'
    ? productIndex?.opensearch?.state
    : productIndex?.solr?.state;
  const inventory = data?.inventory?.last_job || {};
  const dq = data?.data_quality || {};
  const dqSummary = dq?.summary || {};
  const dqLast = dq?.last_run || {};

  return (
    <div className="ahri-dash container-fluid py-3 py-md-4 px-2 px-sm-3">
      <DashboardHero
        loading={loading}
        onRefresh={loadDashboard}
        generatedAt={data?.generated_at}
        ahriTotal={ahri.total_count}
        productTotal={productDb.total}
        completeMatches={matchTotals.complete}
        qualityScore={dqSummary.quality_score}
        lastAhriSync={ahriSync.finished_at || ahriSync.started_at}
        pipelineActive={data?.pipeline?.has_active_job}
      />

      {error ? (
        <div className="alert alert-danger border-0 shadow-sm" role="alert">
          {error}
        </div>
      ) : null}

      {loading && !data ? <LoadingSkeleton /> : null}

      {data ? (
        <>
          <PipelineTimeline
            steps={data.pipeline?.steps}
            getAuthHeaders={getAuthHeaders}
            onRefresh={() => loadDashboard({ silent: true })}
            pipelineMessage={pipelineMessage}
            onPipelineMessage={setPipelineMessage}
            displayTick={pipelineDisplayTick}
          />

          <DashboardDetails
            ahri={ahri}
            ahriSync={ahriSync}
            ahriBreakdown={ahriBreakdown}
            matchTotals={matchTotals}
            ahriSolr={ahriSolr}
            mappingJob={mappingJob}
            productDb={productDb}
            productIndex={productIndex}
            indexState={indexState}
            productV4={productV4}
            inventory={inventory}
            dq={dq}
            dqSummary={dqSummary}
            dqLast={dqLast}
            statusBadgeClass={statusBadgeClass}
          />

          <div className="ahri-dash-footer py-3">
            <Boxes size={14} className="me-1 opacity-50" />
            Pipeline snapshot · {fmtDate(data.generated_at)}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default AhriDashboard;
